import { GridFooterContainer } from '@mui/x-data-grid';
import { useCallback, useMemo } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { Box, Skeleton } from '@/ui/primitives';
import { s } from '../../../theme/tokens';
import { DataGrid } from '../../primitives/DataGrid';
import { resolveGridColumnAlignment, wrapDataGridColumns } from './wrapDataGridColumns';
import type { SpacingToken } from '@/theme/tokens';
import type {
  DataGridProps,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridValidRowModel,
  SxProps,
  Theme,
} from '@/ui/primitives';
import type { ReactElement } from 'react';

type LoadingRow = { id: string; __loading: true };

const skeletonWidthSteps = [38, 44, 51, 58, 65, 72, 79] as const;
const TABLE_SKELETON_HEIGHT = 14;
const TABLE_BOTTOM_PADDING = '10px';
const TABLE_SKELETON_TOP_PADDING = '10px';
const TABLE_SKELETON_TOP_PADDING_COMPACT = '10px';
const TABLE_SKELETON_STACK_GAP = '6px';
const TABLE_SKELETON_NAME_TEXT_GAP = '6px';
const TABLE_SKELETON_NAME_TAG_GAP = TABLE_SKELETON_NAME_TEXT_GAP;
const TABLE_SKELETON_TEXT_PRIMARY_WIDTH = '88%';
const TABLE_SKELETON_TEXT_SECONDARY_WIDTH = '72%';
const TABLE_SKELETON_STATUS_PILL_WIDTH = '72px';
const TABLE_SKELETON_STATUS_META_WIDTH = '44px';
const TABLE_SKELETON_STATUS_TIMESTAMP_WIDTH = '56px';
const TABLE_SKELETON_TAG_WIDTH = '54px';
const TABLE_SKELETON_ACTION_SIZE = '18px';
const TABLE_SKELETON_RADIUS = '999px';
const TABLE_SKELETON_STATUS_INDENT = '2px';
const TABLE_SKELETON_PRIMARY_HEADER_WIDTH = '112px';
const TABLE_SKELETON_ICON_SIZE = '14px';
const TABLE_SKELETON_BREAKDOWN_TOTAL_WIDTH = '54px';
const TABLE_SKELETON_BREAKDOWN_STATUS_WIDTH = '92px';
const TABLE_SKELETON_RUN_LATEST_WIDTH = '56px';
const TABLE_SKELETON_META_PRIMARY_WIDTH = '84px';
const TABLE_SKELETON_META_SECONDARY_WIDTH = '96px';
const TABLE_SKELETON_META_TERTIARY_WIDTH = '72px';
const TABLE_SKELETON_HEADER_LEFT_OFFSET = s('xs');

const resolveHeaderSkeletonMargins = (
  justifyContent: 'flex-start' | 'center' | 'flex-end',
): { ml?: string; mr?: string } => {
  if (justifyContent === 'center') {
    return { ml: 'auto', mr: 'auto' };
  }
  if (justifyContent === 'flex-end') {
    return { ml: 'auto', mr: '0px' };
  }
  return { ml: TABLE_SKELETON_HEADER_LEFT_OFFSET, mr: 'auto' };
};

const cloneSkeletonColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  boxSizing: 'border-box',
  paddingBottom: TABLE_BOTTOM_PADDING,
} as const;

const SkeletonPaginationFooter = (): ReactElement => (
  <GridFooterContainer>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        height: `${componentSizes.dataGrid.footerHeight}px`,
        minHeight: `${componentSizes.dataGrid.footerHeight}px`,
        maxHeight: `${componentSizes.dataGrid.footerHeight}px`,
        boxSizing: 'border-box',
        px: s('md'),
        py: 0,
      }}
    >
      <Skeleton
        variant="rounded"
        width="220px"
        height="14px"
        sx={{ transform: 'none', borderRadius: TABLE_SKELETON_RADIUS }}
      />
    </Box>
  </GridFooterContainer>
);

const hashSkeletonKey = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const resolveCellSkeletonWidth = (params: { id: GridRowId; field: string }): string => {
  const key = `${String(params.id)}:${params.field}`;
  const width = skeletonWidthSteps[hashSkeletonKey(key) % skeletonWidthSteps.length];
  return `${width}%`;
};

const isCloneStructuredCell = (field: string, cellClassName: string | undefined): boolean =>
  cellClassName === 'table-name-cell' ||
  cellClassName === 'table-run-link-cell' ||
  cellClassName === 'table-status-meta-cell' ||
  cellClassName === 'table-breakdown-cell' ||
  cellClassName === 'table-meta-details-cell' ||
  field === 'latestRunStatus' ||
  field === 'testLatestRunBreakdown' ||
  field === 'testStatuses' ||
  field === 'suiteRunPassed' ||
  field === 'testRunPassed' ||
  field === 'suites' ||
  field === 'tests' ||
  field === 'metricsSummary' ||
  field === 'actions';

const renderIconTextLineSkeleton = (args: {
  textWidth: string;
  topMargin?: string;
  withIcon?: boolean;
}): JSX.Element => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: args.withIcon === false ? '0px' : '6px',
      mt: args.topMargin ?? '0px',
      width: '100%',
      minWidth: 0,
      pl: TABLE_SKELETON_STATUS_INDENT,
    }}
  >
    {args.withIcon === false ? null : (
      <Skeleton
        variant="circular"
        width={TABLE_SKELETON_ICON_SIZE}
        height={TABLE_SKELETON_ICON_SIZE}
        sx={{ transform: 'none', flexShrink: 0 }}
      />
    )}
    <Skeleton
      variant="rounded"
      width={args.textWidth}
      sx={{
        height: TABLE_SKELETON_HEIGHT,
        transform: 'none',
        borderRadius: TABLE_SKELETON_RADIUS,
      }}
    />
  </Box>
);

const renderCloneCellSkeleton = (
  params: { id: GridRowId; field: string },
  options: {
    field: string;
    cellClassName: string | undefined;
    cellJustifyContent: 'flex-start' | 'center' | 'flex-end';
  },
): JSX.Element => {
  if (options.cellClassName === 'table-name-cell') {
    const showTagSkeleton = hashSkeletonKey(String(params.id)) % 4 === 0;

    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
          },
        ]}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: TABLE_SKELETON_NAME_TEXT_GAP,
            width: '100%',
          }}
        >
          <Skeleton
            variant="rounded"
            width={TABLE_SKELETON_TEXT_PRIMARY_WIDTH}
            sx={{
              height: TABLE_SKELETON_HEIGHT,
              transform: 'none',
              borderRadius: TABLE_SKELETON_RADIUS,
            }}
          />
          <Skeleton
            variant="rounded"
            width={TABLE_SKELETON_TEXT_SECONDARY_WIDTH}
            sx={{
              height: TABLE_SKELETON_HEIGHT,
              transform: 'none',
              borderRadius: TABLE_SKELETON_RADIUS,
            }}
          />
        </Box>
        {showTagSkeleton ? (
          <Skeleton
            variant="rounded"
            width={TABLE_SKELETON_TAG_WIDTH}
            height={TABLE_SKELETON_HEIGHT}
            sx={{
              transform: 'none',
              borderRadius: TABLE_SKELETON_RADIUS,
              mt: TABLE_SKELETON_NAME_TAG_GAP,
            }}
          />
        ) : null}
      </Box>
    );
  }

  if (options.cellClassName === 'table-run-link-cell') {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            pl: TABLE_SKELETON_STATUS_INDENT,
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_RUN_LATEST_WIDTH}
          height={TABLE_SKELETON_HEIGHT}
          sx={{ transform: 'none', borderRadius: TABLE_SKELETON_RADIUS }}
        />
      </Box>
    );
  }

  if (options.field === 'latestRunStatus') {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING_COMPACT,
            pl: TABLE_SKELETON_STATUS_INDENT,
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_STATUS_PILL_WIDTH}
          height={TABLE_SKELETON_HEIGHT}
          sx={{
            transform: 'none',
            borderRadius: TABLE_SKELETON_RADIUS,
            mt: '2px',
            mb: '6px',
            maxWidth: 'calc(100% - 12px)',
          }}
        />
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_STATUS_META_WIDTH}
          sx={{
            height: TABLE_SKELETON_HEIGHT,
            transform: 'none',
            borderRadius: TABLE_SKELETON_RADIUS,
          }}
        />
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_STATUS_TIMESTAMP_WIDTH}
          sx={{
            height: TABLE_SKELETON_HEIGHT,
            transform: 'none',
            mt: '6px',
            borderRadius: TABLE_SKELETON_RADIUS,
          }}
        />
      </Box>
    );
  }

  if (
    options.cellClassName === 'table-breakdown-cell' ||
    options.field === 'testLatestRunBreakdown' ||
    options.field === 'testStatuses' ||
    options.field === 'suiteRunPassed' ||
    options.field === 'testRunPassed' ||
    options.field === 'suites' ||
    options.field === 'tests'
  ) {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            gap: TABLE_SKELETON_STACK_GAP,
          },
        ]}
      >
        {renderIconTextLineSkeleton({
          textWidth: TABLE_SKELETON_BREAKDOWN_TOTAL_WIDTH,
          withIcon: false,
        })}
        {renderIconTextLineSkeleton({
          textWidth: TABLE_SKELETON_BREAKDOWN_STATUS_WIDTH,
          withIcon: false,
        })}
      </Box>
    );
  }

  if (options.cellClassName === 'table-meta-details-cell') {
    const lineWidths =
      options.field === 'error'
        ? [TABLE_SKELETON_META_PRIMARY_WIDTH, TABLE_SKELETON_META_SECONDARY_WIDTH]
        : [
            TABLE_SKELETON_META_PRIMARY_WIDTH,
            TABLE_SKELETON_META_SECONDARY_WIDTH,
            TABLE_SKELETON_META_TERTIARY_WIDTH,
          ];

    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            gap: TABLE_SKELETON_STACK_GAP,
          },
        ]}
      >
        {lineWidths.map((textWidth, index) => (
          <Box key={index}>{renderIconTextLineSkeleton({ textWidth, withIcon: false })}</Box>
        ))}
      </Box>
    );
  }

  if (options.field === 'metricsSummary') {
    const lineWidths = ['84%', '78%', '82%'] as const;

    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            gap: TABLE_SKELETON_STACK_GAP,
          },
        ]}
      >
        {lineWidths.map((textWidth, index) => (
          <Box key={index}>{renderIconTextLineSkeleton({ textWidth, withIcon: false })}</Box>
        ))}
      </Box>
    );
  }

  if (options.cellClassName === 'table-status-meta-cell') {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING_COMPACT,
            pl: TABLE_SKELETON_STATUS_INDENT,
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_STATUS_PILL_WIDTH}
          height={TABLE_SKELETON_HEIGHT}
          sx={{
            transform: 'none',
            borderRadius: TABLE_SKELETON_RADIUS,
            mt: '2px',
            mb: '6px',
            maxWidth: 'calc(100% - 12px)',
          }}
        />
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_STATUS_META_WIDTH}
          sx={{
            height: TABLE_SKELETON_HEIGHT,
            transform: 'none',
            borderRadius: TABLE_SKELETON_RADIUS,
          }}
        />
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_STATUS_TIMESTAMP_WIDTH}
          sx={{
            height: TABLE_SKELETON_HEIGHT,
            transform: 'none',
            mt: '6px',
            borderRadius: TABLE_SKELETON_RADIUS,
          }}
        />
      </Box>
    );
  }

  if (options.field === 'actions') {
    return (
      <Box
        sx={[
          cloneSkeletonColumnSx,
          {
            pt: TABLE_SKELETON_TOP_PADDING,
            alignItems: 'center',
            justifyContent: 'flex-start',
          },
        ]}
      >
        <Skeleton
          variant="rounded"
          width={TABLE_SKELETON_ACTION_SIZE}
          height={TABLE_SKELETON_ACTION_SIZE}
          sx={{
            transform: 'none',
            borderRadius: TABLE_SKELETON_RADIUS,
          }}
        />
      </Box>
    );
  }

  return (
    <Skeleton
      variant="rounded"
      width={resolveCellSkeletonWidth(params)}
      sx={{
        height: componentSizes.dataGrid.skeletonHeight,
        transform: 'none',
        maxWidth: '100%',
        borderRadius: TABLE_SKELETON_RADIUS,
      }}
    />
  );
};

type SkeletonDataGridProps<T extends GridValidRowModel> = {
  columns: Array<GridColDef<T>>;
  loadingRowCount?: number;
  totalRowCount?: number;
  pageSizeOptions?: number[];
  pageSize?: number;
  paginationModel?: GridPaginationModel;
  rowHeight?: number;
  autoHeight?: boolean;
  showToolbar?: boolean;
  getRowId: (row: T) => GridRowId;
  getRowHeight?: DataGridProps['getRowHeight'];
  cellPaddingY?: SpacingToken;
  columnVisibilityModel?: DataGridProps['columnVisibilityModel'];
  slotProps?: DataGridProps['slotProps'];
  localeText?: DataGridProps['localeText'];
  sx?: DataGridProps['sx'];
  hideFooter?: boolean;
};

const resolvePadding = (token: SpacingToken): string | number => {
  switch (token) {
    case 'none':
      return 0;
    case 'xxxs':
      return s('xxs');
    case 'xxs':
    case 'xs':
      return s('xs');
    case 'sm':
      return s('sm');
    case 'md':
      return s('md');
    case 'lg':
      return s('lg');
    case 'xl':
      return s('xl');
    case '2xl':
      return s('2xl');
    case '3xl':
      return s('3xl');
    case '4xl':
      return s('4xl');
    case '5xl':
      return s('5xl');
    case 'gutter':
      return s('gutter');
    case 'section':
      return s('section');
    default:
      return s('3xl');
  }
};

export const SkeletonDataGrid = <T extends GridValidRowModel>({
  columns,
  loadingRowCount = 10,
  totalRowCount,
  pageSizeOptions = [5, 10, 25, 50],
  pageSize,
  paginationModel,
  rowHeight,
  autoHeight = false,
  showToolbar = false,
  getRowId,
  getRowHeight,
  cellPaddingY,
  columnVisibilityModel,
  slotProps,
  localeText,
  sx,
  hideFooter = false,
}: SkeletonDataGridProps<T>): ReactElement => {
  const resolvedGetRowHeight = rowHeight === undefined ? getRowHeight : undefined;
  const resolvedPageSize =
    pageSize && pageSizeOptions.includes(pageSize) ? pageSize : (pageSizeOptions[0] ?? 5);

  const loadingRows = useMemo<LoadingRow[]>(
    () =>
      Array.from({ length: loadingRowCount }, (_value, index) => ({
        id: `loading-${index}`,
        __loading: true,
      })),
    [loadingRowCount],
  );

  const columnsWithLoading = useMemo(() => {
    const skeletonColumns = columns.map((column) => {
      const { align, headerAlign } = resolveGridColumnAlignment(column);
      const resolvedCellClassName =
        typeof column.cellClassName === 'string' ? column.cellClassName : undefined;
      const isStructuredCloneCell = isCloneStructuredCell(column.field, resolvedCellClassName);
      const cellJustifyContent =
        align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
      const headerJustifyContent =
        column.field === 'actions' ? 'center' : headerAlign === 'right' ? 'flex-end' : 'flex-start';
      return {
        ...column,
        headerAlign: column.field === 'actions' ? 'center' : 'left',
        renderHeader: (params) => {
          if (column.field === 'actions') {
            return <Box sx={{ width: '100%', height: '100%' }} />;
          }
          const computedWidth = params.colDef.computedWidth;
          const widthSource =
            typeof computedWidth === 'number'
              ? computedWidth
              : typeof column.width === 'number'
                ? column.width
                : typeof column.minWidth === 'number'
                  ? column.minWidth
                  : null;
          const headerSkeletonWidth =
            resolvedCellClassName === 'table-name-cell'
              ? TABLE_SKELETON_PRIMARY_HEADER_WIDTH
              : typeof widthSource === 'number'
                ? Math.max(
                    componentSizes.dataGrid.columns.xs,
                    Math.min(widthSource * 0.6, componentSizes.dataGrid.columns.max),
                  )
                : '60%';
          const headerSkeletonMargins = resolveHeaderSkeletonMargins(headerJustifyContent);
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: headerJustifyContent,
                height: '100%',
                width: '100%',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Skeleton
                variant="rounded"
                width={headerSkeletonWidth}
                sx={{
                  height: componentSizes.dataGrid.skeletonHeight,
                  transform: 'none',
                  maxWidth: '100%',
                  borderRadius: TABLE_SKELETON_RADIUS,
                  ...headerSkeletonMargins,
                }}
              />
            </Box>
          );
        },
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: isStructuredCloneCell ? 'flex-start' : 'center',
              justifyContent:
                column.field === 'actions'
                  ? 'center'
                  : isStructuredCloneCell
                    ? 'flex-start'
                    : cellJustifyContent,
              height: '100%',
              width: '100%',
              flex: 1,
              minWidth: 0,
              overflow: isStructuredCloneCell ? 'visible' : 'hidden',
            }}
          >
            {renderCloneCellSkeleton(params, {
              field: column.field,
              cellClassName: resolvedCellClassName,
              cellJustifyContent,
            })}
          </Box>
        ),
      };
    }) as Array<GridColDef<T>>;
    return wrapDataGridColumns(skeletonColumns, {
      getBaseCellSx: () => (cellPaddingY ? { py: resolvePadding(cellPaddingY) } : undefined),
    });
  }, [cellPaddingY, columns]);

  const resolveRowId = useCallback(
    (row: T) => {
      if ('__loading' in row) {
        return (row as unknown as LoadingRow).id;
      }
      return getRowId(row);
    },
    [getRowId],
  );

  const baseSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    '& .MuiDataGrid-footerContainer': {
      borderTop: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
    },
  };
  const resolvedSx: SxProps<Theme> = [
    baseSx,
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as SxProps<Theme>;
  const resolvedPaginationModel = paginationModel ?? { page: 0, pageSize: resolvedPageSize };
  const resolvedRowCount = typeof totalRowCount === 'number' ? totalRowCount : loadingRows.length;
  const defaultSlotProps: NonNullable<DataGridProps['slotProps']> | null =
    typeof totalRowCount === 'number'
      ? null
      : {
          pagination: {
            labelDisplayedRows: () => '',
          },
        };
  const resolvedSlotProps: DataGridProps['slotProps'] = defaultSlotProps
    ? slotProps
      ? {
          ...defaultSlotProps,
          ...slotProps,
          pagination: {
            ...defaultSlotProps.pagination,
            ...(slotProps.pagination ?? {}),
          },
        }
      : defaultSlotProps
    : slotProps;
  const resolvedSlots: DataGridProps['slots'] | undefined =
    typeof totalRowCount === 'number' || hideFooter
      ? undefined
      : {
          footer: SkeletonPaginationFooter,
        };

  return (
    <DataGrid
      rows={loadingRows as unknown as T[]}
      columns={columnsWithLoading}
      disableRowSelectionOnClick
      disableColumnMenu
      hideFooterSelectedRowCount
      hideFooter={hideFooter}
      pageSizeOptions={pageSizeOptions}
      paginationMode="server"
      paginationModel={resolvedPaginationModel}
      rowCount={resolvedRowCount}
      getRowId={resolveRowId}
      initialState={{ pagination: { paginationModel: resolvedPaginationModel } }}
      autoHeight={autoHeight}
      rowHeight={rowHeight}
      getRowHeight={resolvedGetRowHeight}
      showToolbar={showToolbar}
      columnVisibilityModel={columnVisibilityModel}
      slots={resolvedSlots}
      slotProps={resolvedSlotProps}
      localeText={localeText}
      sx={resolvedSx}
    />
  );
};
